/*
 * Copyright (c) Nexign, JSC, 2022-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.19.0
 */

import { Fragment, useLayoutEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { makeSelectLocalStorageIsSubscribersInRoamingPlateHidden } from '@/main/modules/storage/storageSelectors';
import { appHistoryPush } from '@modules/app/appHistory';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageSubscriberMobile,
} from '@modules/internationalization/intSelectors';
import { ONBOARDING_DATA_ATTR } from '@modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum, FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { useTableSettingOpen } from '@modules/table/modules/ColumnSettings/useTableSettingOpen';
import {
    tableContext,
    tableDataCountSelector,
    tableFilterUrlSelector,
    tableIsFastActionPage,
    tableMetaFilterSelector,
    tableSubscribersInRoamingCount,
} from '@modules/table/selectors';
import { wizardStorageClearStepsAction } from '@modules/wizard/wizardActions';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';
import { RoutePath } from '@routes/constants/routesConstants';
import { useCheckCustomRole } from '@utils/useCheckCustomRole';

import { icons } from '../../../assets';
import PageTitle, { renderDropFilterButton, renderSettingButton } from '../../../components/common/PageTitle/PageTitle';
import Guard from '../../../modules/role/Guard';
import { TableDensity, TableExport, useTableFilterApi } from '../../../modules/table';
import { API_SUBSCRIBER_TABLE_META, TABLE_FILTER_AVAILABLE_NAMES } from '../constants';
import { RoamSubscribersPlate } from './RoamSubscribersPlate/RoamSubscribersPlate';
import { RoamSubscribersPlateContainer } from './styled/RoamSubscribersPlateStyled';
import SubscriberExtFilter from './SubscriberMobileExtFilter/components/SubscriberMobileExtFilter';
import SubscriberExtFilterValue from './SubscriberMobileExtFilter/components/SubscriberMobileExtFilterValue';
import SubscriberMobilePersonalDataButton from './SubscriberMobileTitle/SubscriberMobilePersonalDataButton';
import { useTableSubscriberPersonalImport } from './useTableSubscriberPersonalImport';

const TableSubscriberTitle = () => {

    const dispatch = useDispatch();

    const pageTranslation = useSelector(makeSelectTranslationPageSubscriberMobile());
    const translationCommon = useSelector(makeSelectTranslationCommon());
    const isImportSubscribersLimitsAvailable =
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.IMPORT_SUBSCRIBERS_LIMITS));
    const isMainOperationsEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.MAIN_OPERATIONS));
    const count = useSelector(tableDataCountSelector);
    /* Контекст для основных операций*/
    const context = useSelector(tableContext);

    const tableFilterSelector = useMemo(
        () => tableMetaFilterSelector(API_SUBSCRIBER_TABLE_META, TABLE_FILTER_AVAILABLE_NAMES, context), [context]);
    const selectedFilters = useSelector(tableFilterSelector);

    const filterAndSortUrl = useSelector(tableFilterUrlSelector);


    const isSubsInRoamingEnabled =
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.SUBSCRIBERS_IN_ROAMING_NOTIFICATION));
    const subscribersInRoamingCount = useSelector(tableSubscribersInRoamingCount);
    const isSubscribersInRoamingPlateHidden = useSelector(makeSelectLocalStorageIsSubscribersInRoamingPlateHidden());

    // для страниц быстрых действий
    const isFastActionPage = useSelector(tableIsFastActionPage);

    /* Импорт и удаление персональных данных */
    const { isPersonalImportEnabled, openPersonalData } = useTableSubscriberPersonalImport();
    const isCustomRoleCheckAvailable = useCheckCustomRole(true, AuthorityRoleEnum.ROLE_PERSONAL_MANAGE);

    /* Сбросить фильтр */
    const { drop } = useTableFilterApi();

    /* Настройка столбцов */
    const { open: openTableColumnSettings } = useTableSettingOpen({
        locale: {
            // @ts-ignore
            table: pageTranslation.table,
            title: pageTranslation.title.tableSetting,
        },
    });
    const isPersonalDataImportShown = isCustomRoleCheckAvailable && !isFastActionPage;
    const isImportSubscribersLimitsShown = !isImportSubscribersLimitsAvailable && !isFastActionPage;
    const isDropFilter =  isFastActionPage && isMainOperationsEnabled;

    const isSubsInRoamingPlateShown =
        !isFastActionPage && isSubsInRoamingEnabled && !isSubscribersInRoamingPlateHidden;

    const { instructionsLinks: { subsGeneral, subsExport } } = translationCommon;

        /* Импорт абонентов */
    const openWizardMultiSearch = () => {
        dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.MULTI_SEARCH }));
        appHistoryPush(RoutePath.wizardMultiSearch);
    };

    // если мы на странице быстрых действий, то сбрасываем фильты без запроса списка
    // список запросится сам при инициализации
    useLayoutEffect(() => {
        if (isDropFilter && filterAndSortUrl) {
            drop(undefined, true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[isDropFilter, filterAndSortUrl]);

    return (
        <Fragment>
            <PageTitle
                count={count}
                dataName="subscriber"
                instructionLink={subsGeneral}
                title={pageTranslation.title.header}
            >
                {
                    /* Сбросить фильтр */
                    !!Object.keys(selectedFilters).length
                    && renderDropFilterButton({
                        onClick: drop,
                        value: pageTranslation.title.dropFilters,
                    })
                }
                <Guard accessKey={AccessComponentKeyEnum.TitleButtonWizardMultiSearch}>
                    <>
                        {
                            isImportSubscribersLimitsShown &&
                            /* Импорт абонентов */
                            renderSettingButton({
                                id: 1,
                                dataName: 'wizard-multi-search-button',
                                iconLeft: icons.uploadIcon,
                                onClick: openWizardMultiSearch,
                                value: pageTranslation.title.wizardMultiSearch,
                            })
                        }
                    </>
                </Guard>
                <SubscriberExtFilter translation={pageTranslation}/>
                {
                    /* Импорт и удаление персональных данных */
                    isPersonalDataImportShown && (
                        isPersonalImportEnabled
                            ? <div
                                className="page-title__button"
                                {...{ [ONBOARDING_DATA_ATTR]: EOnboardingComponentsId.subscriberTablePersonalImport }}
                            >
                                <SubscriberMobilePersonalDataButton
                                    translation={
                                        isImportSubscribersLimitsAvailable
                                            ? pageTranslation.title.importSettingAndLimits
                                            : pageTranslation.title.importSetting
                                    }
                                    onSelectHandler={openPersonalData}
                                />
                            </div>
                            : renderSettingButton({
                                id: 2,
                                onboarding: EOnboardingComponentsId.subscriberTablePersonalImport,
                                dataName: 'Import personal data',
                                iconLeft: icons.uploadIcon,
                                onClick: openPersonalData,
                                value: pageTranslation.title.importSettingOld,
                            }))
                }
                {
                    /* Настройка столбцов */
                    renderSettingButton({
                        id: 3,
                        dataName: 'Settings of columns',
                        iconLeft: icons.settingIcon,
                        onboarding: EOnboardingComponentsId.subscriberTableSettings,
                        onClick: openTableColumnSettings,
                        value: pageTranslation.title.tableSetting.titleBtn,
                    })
                }
                {!isFastActionPage &&
                    <TableExport
                        instructionLink={subsExport}
                        url="subscriber/mobile/export"
                    />
                }
                <TableDensity/>
            </PageTitle>
            <SubscriberExtFilterValue translation={pageTranslation}/>
            {
                    isSubsInRoamingPlateShown &&
                    !!subscribersInRoamingCount &&
                    <RoamSubscribersPlateContainer>
                        <RoamSubscribersPlate/>
                    </RoamSubscribersPlateContainer>
            }
        </Fragment>

    );
};

export default TableSubscriberTitle;
